import { useEffect, useMemo, useState } from 'react';
import type { LiberacaoTabelaResponse } from '@triade/contracts';
import { useEscopoItem, useSalvarEscopo } from '@/api/liberacao';
import { usePode } from '@/api/me';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api';
import { ErroFormacao } from './avisos-formacao';

function mensagemErro(e: unknown): string {
  return e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API';
}

/** Chave de uma combinacao (tabela x condicao). Ids sao cuid (sem "|"). */
const chave = (tabelaId: string, condicaoId: string) => `${tabelaId}|${condicaoId}`;

/**
 * "Não liberar mesmo com preço" (Sessao 36, Acoes em lote): a liberacao e automatica (preco preenchido = liberado); aqui se marcam as
 * EXCECOES (tabela x condicao em que o item NAO e liberado). Salvar substitui as excecoes inteiras (PUT) e vale na hora. Nada e calculado aqui.
 */
export function EscopoItem({ itemId, tabelas }: { itemId: string; tabelas: LiberacaoTabelaResponse[] }) {
  const { pode } = usePode();
  const podeGravar = pode('praticado.gravar');
  const escopo = useEscopoItem(itemId);
  const salvar = useSalvarEscopo(itemId);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [aviso, setAviso] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const salvos = useMemo(() => new Set((escopo.data?.naoLiberados ?? []).map((l) => chave(l.tabelaId, l.condicaoId))), [escopo.data]);
  // Quando a API devolve um escopo novo (carga inicial ou depois de salvar), a marcacao volta a refletir o servidor.
  useEffect(() => setSel(new Set(salvos)), [salvos]);

  // Colunas: condicoes (na ordem em que aparecem); linhas: so tabelas com condicoes (as indisponiveis ficam de fora).
  const colunas = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of tabelas) for (const c of t.condicoes) if (!m.has(c.condicaoId)) m.set(c.condicaoId, c.condicaoNome);
    return [...m].map(([condicaoId, condicaoNome]) => ({ condicaoId, condicaoNome }));
  }, [tabelas]);
  const linhas = tabelas.filter((t) => t.condicoes.length > 0);
  const total = linhas.reduce((n, t) => n + t.condicoes.length, 0);

  const alterado = sel.size !== salvos.size || [...sel].some((k) => !salvos.has(k));

  function alternar(k: string) {
    setSel((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(k)) proximo.delete(k);
      else proximo.add(k);
      return proximo;
    });
    setAviso(null);
    setErro(null);
  }

  async function gravar() {
    setErro(null);
    setAviso(null);
    const liberados = [...sel].map((k) => {
      const [tabelaId = '', condicaoId = ''] = k.split('|');
      return { tabelaId, condicaoId };
    });
    try {
      await salvar.mutateAsync({ naoLiberados: liberados });
      setAviso(liberados.length === 0 ? 'Exceções salvas: nenhuma' : 'Exceções salvas');
    } catch (e) {
      setErro(mensagemErro(e));
    }
  }

  return (
    <section aria-label="Não liberar mesmo com preço" data-testid="escopo-item" className="flex flex-col gap-3 rounded-xl border border-border p-4">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-body font-semibold">Não liberar mesmo com preço</h3>
          <p className="mt-0.5 text-small text-ink-secondary">
            O item com preço preenchido é liberado automaticamente. Marque as tabelas e condições em que ele NÃO deve ser liberado, mesmo com preço (exceção).
          </p>
        </div>
        {sel.size === 0 ? (
          <span className="rounded-full bg-line-soft px-2.5 py-0.5 text-caption font-medium text-ink-body">Sem exceções</span>
        ) : (
          <span className="rounded-full bg-line-soft px-2.5 py-0.5 text-caption font-medium text-ink-body">
            Não liberado em {sel.size} de {total} {total === 1 ? 'combinação' : 'combinações'}
          </span>
        )}
      </header>

      {escopo.isError && <ErroFormacao erro={escopo.error} onTentar={() => void escopo.refetch()} />}
      {escopo.isPending && !escopo.isError && <p role="status" className="text-small text-ink-muted">Carregando exceções…</p>}

      {escopo.data && linhas.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-small">
            <thead>
              <tr className="border-b border-border text-left text-caption text-ink-muted">
                <th scope="col" className="px-2 py-2 font-medium">
                  Tabela
                </th>
                {colunas.map((c) => (
                  <th key={c.condicaoId} scope="col" className="px-2 py-2 font-medium">
                    {c.condicaoNome}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {linhas.map((t) => (
                <tr key={t.tabelaId} className="border-b border-border">
                  <th scope="row" className="px-2 py-2 text-left font-medium">
                    {t.tabelaNome}
                  </th>
                  {colunas.map((c) => {
                    const existe = t.condicoes.some((x) => x.condicaoId === c.condicaoId);
                    if (!existe) {
                      return (
                        <td key={c.condicaoId} className="px-2 py-2 text-ink-disabled">
                          —
                        </td>
                      );
                    }
                    const k = chave(t.tabelaId, c.condicaoId);
                    return (
                      <td key={c.condicaoId} className="px-2 py-2">
                        <input
                          type="checkbox"
                          className="size-4 accent-primary"
                          aria-label={`${t.tabelaNome} · ${c.condicaoNome}`}
                          checked={sel.has(k)}
                          disabled={!podeGravar || salvar.isPending}
                          onChange={() => alternar(k)}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {escopo.data && linhas.length === 0 && <p className="text-small text-ink-muted">Nenhuma tabela com condições para marcar.</p>}

      <p className="text-caption text-ink-muted">O que está marcado não aparece na lista do vendedor e a API recusa no orçamento.</p>

      {erro && (
        <p role="alert" className="rounded-xl border border-destructive/30 bg-danger-bg px-4 py-3 text-small text-danger-ink">
          Não foi possível salvar as exceções: {erro}
        </p>
      )}
      {aviso && (
        <p role="status" className="text-small text-ink-secondary">
          {aviso}
        </p>
      )}

      {podeGravar && (
        <div className="flex justify-end">
          <Button type="button" size="sm" disabled={!alterado || salvar.isPending || !escopo.data} onClick={() => void gravar()}>
            Salvar exceções
          </Button>
        </div>
      )}
    </section>
  );
}
